import { authProvider } from '../providers/authProvider'
import RequestFactory from './RequestFactory'

const uploadCertificateAttachmentBatches = async (baseUrl, endpoint, batches) => {
  if (!batches || batches.length === 0) return { isSuccess: true }

  let response

  for (const batch of batches) {
    response = await RequestFactory.upload(baseUrl, endpoint, batch)

    if (!response?.isSuccess) {
      return response
    }
  }

  return response
}

const certificateUrl = process.env.REACT_APP_CERTIFICATE_URL
const GMAE2EURL = process.env.REACT_APP_GMAE2E_URL

const getCertificateAttributesEndpoint = `/ipintegration/get-ip-certificate-attributes-v2`
const getCertificateSchemeEndpoint = `/api/certificate/get-ip-certificate-scheme`
const getCertificateSchemeBaseUrl = GMAE2EURL
const ipWritebackUrl = '/ipintegration/ip-certificate-writeback-v3'

const CertificateListService = {
  getCertificateScheme: async data =>
    await RequestFactory.get(`${getCertificateSchemeBaseUrl}`, `${getCertificateSchemeEndpoint}`),
  getCertificateListData: async data =>
    await RequestFactory.post(certificateUrl, '/ipintegration/get-ip-certificate-list', data),
  getIpProductListData: async data =>
    await RequestFactory.post(certificateUrl, '/ipintegration/get-ip-product-list', data),
  getCertificateAttributes: async (scheme, hierarchyId, isCO = false, isDecision = false) =>
    await RequestFactory.get(
      certificateUrl,
      `${getCertificateAttributesEndpoint}?certificateScheme=${scheme}&hierarchyId=${hierarchyId}&isCo=${isCO}&isDecision=${isDecision}`
    ),
  getCertifcateDecisionSummary: async (scheme, hierarchyId, isCO = false, isDecision = false) =>
    await RequestFactory.get(
      certificateUrl,
      `/ipintegration/get-ip-certificate-decision-summary?certificateScheme=${scheme}&hierarchyId=${hierarchyId}&isCo=${isCO}&isDecision=${isDecision}`
    ),
  getCertifcateParties: async (scheme, hierarchyId) =>
    await RequestFactory.get(
      certificateUrl,
      `/ipintegration/get-ip-certificate-parties?certificateScheme=${scheme}&hierarchyId=${hierarchyId}`
    ),
  getCertificateStatistics: async () =>
    await RequestFactory.get(certificateUrl, '/ipintegration/get-ip-certificate-statistics'),
  getIpCertificateRefiners: async (refiner, search, config) =>
    await RequestFactory.get(
      certificateUrl,
      `/ipintegration/get-ip-certificate-refiners?refiner=${refiner}&search=${search}`,
      config
    ),
  getIpAttributesDropdownList: async (attributeType, search) =>
    await RequestFactory.get(
      certificateUrl,
      `/ipintegration/get-ip-attributes-dropdown-list?attributeType=${attributeType}&search=${search}`
    ),
  getIpStandardDetails: async (attributeType, standardScopeId) =>
    await RequestFactory.get(
      certificateUrl,
      `/ipintegration/get-ip-attributes-dropdown-list?attributeType=${attributeType}&schemeScopeStandardId=${standardScopeId}`
    ),
  deleteIPCertificate: async certificateId =>
    await RequestFactory.post(certificateUrl, '/ipintegration/delete', {
      certificateId,
    }),
  addCertAttachment: async (formData, uploadProgressEvent) =>
    await RequestFactory.upload(
      certificateUrl,
      `/certificate/add-attachment`,
      formData,
      uploadProgressEvent
    ),
  deleteAttachment: async data =>
    await RequestFactory.post(certificateUrl, '/certificate/delete-attachment', data),
  saveFavorites: async data =>
    await RequestFactory.post(certificateUrl, '/favorites/upsert-view-favorite-settings', data),
  getFavorites: async id =>
    await RequestFactory.get(
      certificateUrl,
      `/favorites/list-saved-view-favorites?viewTypeId=${id}`
    ),
  getCertificateDetails: async certId =>
    await RequestFactory.get(
      certificateUrl,
      `/ipintegration/get-ip-certificate-details?certificateId=${certId}`
    ),
  ipCertificateWriteback: async data =>
    await RequestFactory.post(certificateUrl, '/ipintegration/ip-certificate-writeback', data),
  ipCertificateWritebackV2: async data =>
    await RequestFactory.post(certificateUrl, ipWritebackUrl, data),
  uploadTRFFile: async data =>
    await RequestFactory.upload(GMAE2EURL, '/api/certificate/trf-extraction', data),
  certificateSaveAndUploadECM: async data =>
    await RequestFactory.upload(GMAE2EURL, '/api/certificate/create-certificate', data),
  certificateSaveAndUploadECMBatch: async batches =>
    await uploadCertificateAttachmentBatches(
      GMAE2EURL,
      '/api/certificate/create-certificate',
      batches
    ),
  certificateUpdateAndUploadECM: async data =>
    await RequestFactory.upload(GMAE2EURL, '/api/certificate/update-certificate', data),
  certificateUpdateAndUploadECMBatch: async batches =>
    await uploadCertificateAttachmentBatches(
      GMAE2EURL,
      '/api/certificate/update-certificate',
      batches
    ),
  certificateAttachmentSaveLinkUploadECM: async data =>
    await RequestFactory.upload(
      GMAE2EURL,
      '/api/certificate/certificate-document/link-to-wb-and-ecm',
      data
    ),
  certificateAttachmentSaveLinkUploadECMBatch: async batches =>
    await uploadCertificateAttachmentBatches(
      GMAE2EURL,
      '/api/certificate/certificate-document/link-to-wb-and-ecm',
      batches
    ),
  certificateAttachmentSingleLinkUploadECM: async data =>
    await RequestFactory.post(
      GMAE2EURL,
      '/api/certificate/certificate-document/single-link-to-wb-and-ecm',
      data
    ),
  activateCertificate: async data =>
    await RequestFactory.post(certificateUrl, '/ipintegration/activate-certificate', data),
  withdrawCertificate: async data =>
    await RequestFactory.post(certificateUrl, '/ipintegration/withdraw-certificate', data),
  modifyCertificate: async data =>
    await RequestFactory.post(certificateUrl, '/ipintegration/modify-certificate', data),
  modifyCertificateV2: async data =>
    await RequestFactory.post(certificateUrl, '/ipintegration/modify-certificate-v2', data),
  updateAttachments: async data => {
    await RequestFactory.post(certificateUrl, '/ipintegration/save-attached-file', data)
  },
  uploadBulkImportTemplate: async (formData, uploadProgressEvent) =>
    await RequestFactory.upload(
      certificateUrl,
      `/ipintegration/bulk_upload_certificate`,
      formData,
      uploadProgressEvent
    ),
  exportToSpreadSheet: async data =>
    await RequestFactory.download(certificateUrl, `/ipintegration/export-ip-certificate`, data),
  deleteStoreCertificate: async data =>
    await RequestFactory.post(certificateUrl, '/certificate/delete-store-certificate', data),
  remapModel: async data =>
    await RequestFactory.post(certificateUrl, '/ipintegration/ip-remap-models', data),
  downloadCertificateAttachment: async data =>
    await RequestFactory.download(
      certificateUrl,
      '/ipintegration/download-certificate-attachment',
      data
    ),
  generateCertficateNumber: async data =>
    await RequestFactory.post(certificateUrl, '/ipintegration/generate-certificate-number', data),
  getCertificateProjectHistory: async data =>
    await RequestFactory.post(
      certificateUrl,
      '/ipintegration/get-certificate-project-history?certificateId=' + data,
      data
    ),
  getCertificateDocuments: async certificateId =>
    await RequestFactory.get(GMAE2EURL, `/api/certificate/certificate-document/${certificateId}`),
  getCertificateDocumentsByUniqueCertificateId: async uniqueCertificateId =>
    await RequestFactory.get(
      GMAE2EURL,
      `/api/certificate/certificate-document/uniqId/${uniqueCertificateId}`
    ),
  getCertificateDocumentsByDocumentId: async certificateDocumentId =>
    await RequestFactory.get(
      GMAE2EURL,
      `/api/certificate/certificate-document/docId/${certificateDocumentId}`
    ),
  getCertificateDocumentsPaginated: async data =>
    await RequestFactory.post(GMAE2EURL, `/api/certificate/certificate-document/paginated`, data),
  syncDocument: async uniqueCertificateId =>
    await RequestFactory.post(
      GMAE2EURL,
      `/api/certificate/sync-document/${uniqueCertificateId}`,
      {}
    ),
  getCertificateDocumentsFailedEcmUpload: async (data, schemeEcmType) =>
    await RequestFactory.post(
      GMAE2EURL,
      `/api/certificate/certificate-document/upload-failed/list?schemeEcmType=${schemeEcmType}`,
      data
    ),
  getCertificateDocumentsFailedEcmUploadCount: async schemeEcmType =>
    await RequestFactory.get(
      GMAE2EURL,
      `/api/certificate/certificate-document/upload-failed/count?schemeEcmType=${schemeEcmType}`
    ),
  downloadCertificateDocument: async documentId =>
    await RequestFactory.downloadGet(
      GMAE2EURL,
      `/api/certificate/certificate-document/download/${documentId}`
    ),
  getCertificateDetailByWorkbenchCertificateId: async workbenchCertificateId =>
    await RequestFactory.get(
      GMAE2EURL,
      '/api/certificate/certificate-detail-by-wb-cert/' + workbenchCertificateId
    ),
  getCertificateSchemeDetailByName: async schemeName =>
    await RequestFactory.get(
      GMAE2EURL,
      '/api/certificate/get-certificate-scheme-detail-byname/' + schemeName
    ),
  unlinkEcmDocument: async model =>
    await RequestFactory.post(GMAE2EURL, '/api/certificate/ecm-document/unlink', model),
  deleteFileFromBlob: async fileId =>
    await RequestFactory.post(GMAE2EURL, `/api/certificate/certificate-document/delete/${fileId}`),
  getCertificateDetailsForActivation: async workbenchCertificateId =>
    await RequestFactory.get(
      GMAE2EURL,
      `/api/certificate/certificate-for-activation/${workbenchCertificateId}`
    ),
  updateActivatedCertificateDetails: async workbenchCertificateId =>
    await RequestFactory.patch(
      GMAE2EURL,
      `/api/certificate/certificate-detail/update-activated-certificate-detail/${workbenchCertificateId}`
    ),
  getHasCertificateBeenActive: async certificateId => {
    return await RequestFactory.get(
      GMAE2EURL,
      '/api/certificate/has-certificate-been-active?certificateId=' + certificateId
    )
  },
  linkWb: async model =>
    await RequestFactory.post(GMAE2EURL, '/api/certificate/ecm-document/link', model),
  reuploadAndLinkDocsBatch: async (param, schemeEcmType) =>
    await RequestFactory.post(
      GMAE2EURL,
      `/api/certificate/certificate-document/reupload-and-link-docs-batch?schemeEcmType=${schemeEcmType}`,
      param
    ),
  deleteBatchCertificateDocumentsFailedEcmUpload: async (data, schemeEcmType) =>
    await RequestFactory.post(
      GMAE2EURL,
      `/api/certificate/certificate-document/delete-batch?schemeEcmType=${schemeEcmType}`,
      data
    ),
  getDuplicateDocuments: async data =>
    await RequestFactory.post(
      GMAE2EURL,
      '/api/certificate/certificate-document/documents-duplicate',
      data
    ),
  getCertificateDetailsWithDocuments: async certificateId =>
    await RequestFactory.get(GMAE2EURL, `/api/certificate/certificate-detail/${certificateId}`),

  getCertificateDetailsRedirect: async payload =>
    await RequestFactory.get(
      GMAE2EURL,
      `/api/certificate/get-certificate-details-redirect?e2EProjectId=${payload.e2EProjectId}&e2EProjectLineId=${payload.e2EProjectLineId}&e2EProjectLineFlexTaskId=${payload.e2EProjectLineFlexTaskId}`
    ),

  // #region R/P Library Services
  certificateRPSaveAndUploadECM: async data =>
    await RequestFactory.upload(GMAE2EURL, '/api/certificate/create-certificate-rp', data),
  certificateRPSaveAndUploadECMBatch: async batches =>
    await uploadCertificateAttachmentBatches(
      GMAE2EURL,
      '/api/certificate/create-certificate-rp',
      batches
    ),

  certificateRPUpdateAndUploadECM: async data =>
    await RequestFactory.upload(GMAE2EURL, '/api/certificate/update-certificate-rp', data),
  certificateRPUpdateAndUploadECMBatch: async batches =>
    await uploadCertificateAttachmentBatches(
      GMAE2EURL,
      '/api/certificate/update-certificate-rp',
      batches
    ),
  getCertificateRPDocumentsByUniqueCertificateId: async uniqueCertificateId =>
    await RequestFactory.get(
      GMAE2EURL,
      `/api/certificate/certificate-rp-document/uniqId/${uniqueCertificateId}`
    ),
  getCertificateRPDocumentsPaginated: async data =>
    await RequestFactory.post(
      GMAE2EURL,
      `/api/certificate/certificate-rp-document/paginated`,
      data
    ),
  certificateAttachmentRPSaveLinkUploadECM: async data =>
    await RequestFactory.upload(
      GMAE2EURL,
      '/api/certificate/certificate-document/link-to-wb-and-ecm-rp',
      data
    ),
  certificateAttachmentRPSaveLinkUploadECMBatch: async batches =>
    await uploadCertificateAttachmentBatches(
      GMAE2EURL,
      '/api/certificate/certificate-document/link-to-wb-and-ecm-rp',
      batches
    ),
  getAssetDetails: async assetId =>
    await RequestFactory.get(certificateUrl, `/ipintegration/asset-details/${assetId}`),
  getCertificateTestReportDocument: async data =>
    await RequestFactory.post(GMAE2EURL, '/api/certificate/get-test-report-documents', data),
  manageDocuments: async data =>
    await RequestFactory.post(certificateUrl, `/ipintegration/asset-image-upload`, data),
  assetDocuments: async data =>
    await RequestFactory.post(certificateUrl, `/ipintegration/assets-documents`, data),
  getAssetImage: async data =>
    await RequestFactory.post(certificateUrl, `/ipintegration/asset-image`, data),
  downloadAssetImage: async data =>
    await RequestFactory.downloadRaw(certificateUrl, '/ipintegration/download-asset-image', data),
  getCertificateDocumentsByCertificateScheme: async certificateScheme =>
    await RequestFactory.get(
      GMAE2EURL,
      '/api/certificate/get-documents-by-certificate-scheme?certificateScheme=' + certificateScheme
    ),
  getAssetProductRating: async (assetId, productType) =>
    await RequestFactory.get(
      certificateUrl,
      `/ipintegration/asset-product-rating/${assetId}?productType=${encodeURIComponent(productType)}`
    ),
  getQRCodeSupportedCertSchemes: async () =>
    await RequestFactory.get(GMAE2EURL, '/api/certificate/qr-code-supported-cert-schemes'),
  getProductTypes: async () =>
    await RequestFactory.get(GMAE2EURL, '/api/certificate/get-ip-product-type'),
  getIpModels: async data =>
    await RequestFactory.post(GMAE2EURL, '/api/certificate/get-ip-models-paginated', data),
  getCompanyList: async (
    customerName,
    partySiteNumber,
    oracleAccountNumber,
    itemsPerPage,
    currentPageNumber
  ) =>
    await RequestFactory.get(
      GMAE2EURL,
      `/api/certificate/get-company-list?partysiteNumber=${partySiteNumber}&accountNumber=${oracleAccountNumber}&partyName=${customerName}&itemsPerPage=${itemsPerPage}&currentPageNumber=${currentPageNumber}`
    ),
  bulkUnlinkEcmDocument: async model =>
    await RequestFactory.post(GMAE2EURL, '/api/certificate/ecm-document/bulk-unlink', model),
  bulkLinkEcmDocument: async model =>
    await RequestFactory.post(GMAE2EURL, '/api/certificate/ecm-document/bulk-link', model),
  getAssets: async model =>
    await RequestFactory.post(GMAE2EURL, '/api/certificate/assets', model),
  getCompannyAddresses: async model =>
    await RequestFactory.post(GMAE2EURL, '/api/certificate/companyAddresses', model),
  //#endregion
}

export default CertificateListService
